import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'bodyup_client_token';
const REF_CODE_KEY = 'bodyup_ref_code';

const ClientAuthContext = createContext(null);
export const useClientAuth = () => useContext(ClientAuthContext);

const parseError = (d, fallback) => {
  const detail = d && d.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || '').filter(Boolean).join(' ') || fallback;
  return fallback;
};

export const ClientAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Programme de parrainage : capture le code ?ref=CODE au 1er atterrissage (page vitrine),
  // persisté jusqu'à l'inscription (le visiteur peut naviguer plusieurs pages avant de créer
  // son compte) — jamais écrasé une fois capturé, même si le lien est recliqué sans ?ref.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) localStorage.setItem(REF_CODE_KEY, ref.trim().toUpperCase());
  }, []);

  const authFetch = useCallback(
    (path, opts = {}) =>
      fetch(`${BACKEND_URL}${path}`, {
        ...opts,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(opts.headers || {}),
        },
      }),
    [token]
  );

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    authFetch('/api/auth/me')
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => { if (active) setUser(d.user); })
      .catch(() => {
        if (active) {
          setUser(null);
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        }
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [token, authFetch]);

  const login = async (email, password) => {
    const r = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(parseError(d, 'Connexion impossible.'));
    localStorage.setItem(TOKEN_KEY, d.token);
    setToken(d.token);
    setUser(d.user);
    return d.user;
  };

  const register = async (email, password, name, language = 'fr') => {
    const referral_code = localStorage.getItem(REF_CODE_KEY) || '';
    const r = await fetch(`${BACKEND_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, language, referral_code }),
    });
    const d = await r.json();
    if (!r.ok) { const err = new Error(parseError(d, 'Inscription impossible.')); err.status = r.status; throw err; }
    localStorage.removeItem(REF_CODE_KEY);
    localStorage.setItem(TOKEN_KEY, d.token);
    setToken(d.token);
    setUser(d.user);
    return d.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  return (
    <ClientAuthContext.Provider value={{ user, loading, token, login, register, logout, authFetch, isAuthed: !!user }}>
      {children}
    </ClientAuthContext.Provider>
  );
};
